import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { Waves, Heart, Leaf, Sparkles, RefreshCw } from "lucide-react";
import { api } from "../../services/api";
import AstriaJapanV2Shell, {
  SHELL_COLORS_V2,
  JP2_FONT,
} from "./AstriaJapanV2Shell";
import { getStoredAstriaJapanDob } from "../../utils/astriaJapanKyusei";
import { useJapanLanguage } from "./japanLanguage";
import { ls, LC, L, useLnLanguage } from "../../utils/lnText";

// ─── Astria Japan Timing Flow Result (V2) — user opens tab, sees Daily
// Timing, Relationship Soft Timing, Minimal Diary prompt, and Gentle Luck on
// one screen, all at once, no chat required. Same UI Style Guide as
// KyuseiJapanResult.tsx (warm sakura/coral v2 lane).
// Section backgrounds: dailyTimingFlow #FBF3EF, relationshipSoftTiming
// #FDECE8, gentleLuckFlow #F2F7EE.
// Language support: Japanese or English based on user preference

const JP_FONT = JP2_FONT;
const C = {
  ...SHELL_COLORS_V2,
  relationshipBg: "#FDECE8",
  luckBg: "#F2F7EE",
  textPrimary: "#2B211D",
  textSecondary: "#5A4A42",
  textMuted: "#8A7A73",
  gradientStart: "#FDF7F5",
  gradientEnd: "#FFF0EC",
};

const DIARY_MAX_LENGTH = 120;

interface Star {
  id: number;
  code: string;
  primaryColor: string;
  secondaryColor: string;
}

interface TimingResponse {
  success: boolean;
  starId: number;
  star: Star;
  view: {
    header: { title: string; subtext: string };
    sections: Array<{ id: string; title: string; type: string; data: any }>;
  };
}

const fadeUp = {
  hidden: { opacity: 0, y: 14 },
  visible: (i: number) => ({
    opacity: 1,
    y: 0,
    transition: {
      delay: i * 0.08,
      duration: 0.35,
      ease: [0.22, 1, 0.36, 1] as [number, number, number, number],
    },
  }),
};

const SectionBlock: React.FC<{
  index: number;
  title: string;
  icon?: React.ReactNode;
  iconBg?: string;
  background?: string;
  children: React.ReactNode;
}> = ({ index, title, icon, iconBg, background, children }) => (
  <motion.div
    custom={index}
    variants={fadeUp}
    initial="hidden"
    animate="visible"
    style={{
      background: background || C.section,
      borderRadius: 16,
      padding: "28px 32px",
      border: "1px solid rgba(224,121,107,0.08)",
      boxShadow: "0 2px 12px rgba(224,121,107,0.06)",
    }}
  >
    <div className="flex items-center gap-4" style={{ marginBottom: 16 }}>
      {icon && (
        <div
          className="flex-shrink-0 flex items-center justify-center"
          style={{
            width: 40,
            height: 40,
            borderRadius: "50%",
            background: iconBg || "rgba(212,115,93,0.12)",
          }}
        >
          {icon}
        </div>
      )}
      <div
        style={{
          fontSize: 19,
          fontWeight: 500,
          color: C.sectionTitleColor,
          letterSpacing: "0.02em",
        }}
      >
        {title}
      </div>
    </div>
    {children}
  </motion.div>
);

const JPTimingFlowResult: React.FC = () => {
  useLnLanguage();
  const navigate = useNavigate();
  const [data, setData] = useState<TimingResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [diaryEntry, setDiaryEntry] = useState("");
  const [diarySaved, setDiarySaved] = useState(false);


  const language = useJapanLanguage();

  // Language-specific content
  const content = {
    ja: {
      brand: "心のタイミング",
      timing: "今日のタイミング",
      dailyFlow: "今日の心の流れ",
      relationship: "関係のタイミング",
      gentleLuck: "やわらかな運の流れ",
      diary: "今日のひとこと日記",
      diaryPrompt: "今日、心に残った小さな瞬間をつづけていきたい。",
      diaryHint:
        "ヒント：「安心した瞬間」「ふっと笑った瞬間」など、短くても大丈夫ですよ。",
      diaryPlaceholder: "今日、心に残った小さな瞬間を書いてみてください…",
      saveDiary: "そっと残す",
      diarySaved: "保存しました ✓",
      refresh: "もう一度",
      error: "タイミングの情報が見つかりませんでした。",
      loading: "読み込み中...",
      back: "今日のタイミングを見る",
      footer: "あなたの心に、そっと寄り添う時間を。",
      subtext:
        "強い占いではなく、心の流れとタイミングを静かに感じるための画面です。",
    },
    en: {
      brand: "Heart's Timing",
      timing: "Today's Timing",
      dailyFlow: "Today's Heart Flow",
      relationship: "Relationship Timing",
      gentleLuck: "Gentle Luck Flow",
      diary: "Today's One-Line Diary",
      diaryPrompt:
        "I want to capture the small moments that touched my heart today.",
      diaryHint:
        "Hint: 'A moment of peace' or 'A time I smiled' - even short entries are fine.",
      diaryPlaceholder:
        "Write about a small moment that touched your heart today...",
      saveDiary: "Gently Save",
      diarySaved: "Saved ✓",
      refresh: "Review Again",
      error: ls("Timing information not found."),
      loading: ls("Loading..."),
      back: "View Today's Timing",
      footer: "A gentle moment for your heart.",
      subtext:
        "A space to quietly feel the flow and timing of your heart, not a strong fortune-telling.",
    },
  };

  const t = LC(content, language);

  useEffect(() => {
    const raw = sessionStorage.getItem("astriaJapanKyuseiTiming");
    if (raw) {
      try {
        setData(JSON.parse(raw));
        return;
      } catch {
        // fall through to a fresh fetch below
      }
    }

    const storedDob = getStoredAstriaJapanDob();
    if (!storedDob) {
      setError(t.error);
      return;
    }

    api
      .post("/backend/astria-japan-kyusei/timing", { dob: storedDob })
      .then((response) => {
        if (response.data?.success) {
          sessionStorage.setItem(
            "astriaJapanKyuseiTiming",
            JSON.stringify(response.data),
          );
          setData(response.data);
        } else {
          setError(t.error);
        }
      })
      .catch(() => setError(t.error));
  }, [t.error]);

  const handleNewCheckIn = () => {
    sessionStorage.removeItem("astriaJapanKyuseiDaily");
    sessionStorage.removeItem("astriaJapanKyuseiTiming");
    navigate("/astria-japan/timing");
  };

  const handleSaveDiary = () => {
    if (!diaryEntry.trim()) return;
    setDiarySaved(true);
    setTimeout(() => setDiarySaved(false), 2500);
  };

  if (error) {
    return (
      <div
        className="min-h-screen flex items-center justify-center px-4"
        style={{ background: C.bg, fontFamily: JP_FONT }}
      >
        <div className="text-center">
          <p className="text-sm mb-4" style={{ color: "#B4574A" }}>
            {error}
          </p>
          <button
            onClick={() => navigate("/astria-japan/timing")}
            className="px-6 py-2.5 rounded-full text-white text-xs font-semibold"
            style={{ background: C.accent }}
          >
            {t.back}
          </button>
        </div>
      </div>
    );
  }

  if (!data) {
    return (
      <div
        className="min-h-screen flex items-center justify-center"
        style={{ background: C.bg }}
      >
        <div
          className="w-8 h-8 rounded-full border-2 animate-spin"
          style={{ borderColor: `${C.accent}33`, borderTopColor: C.accent }}
        />
      </div>
    );
  }

  const { view, star } = data;
  const byId = (id: string) => view.sections.find((s) => s.id === id);

  const dailyTiming = byId("dailyTimingFlow")?.data?.message;
  const relationshipTiming = byId("relationshipSoftTiming")?.data?.message;
  const diaryFlow = byId("minimalDiaryFlow")?.data;
  const gentleLuck = byId("gentleLuckFlow")?.data?.message;

  return (
    <AstriaJapanV2Shell active="timing">
      {/* ─── Header ────────────────────────────────────────────────────── */}
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        style={{
          marginBottom: 28,
          padding: "28px 32px",
          background: `linear-gradient(135deg, ${C.gradientStart} 0%, ${C.gradientEnd} 100%)`,
          borderRadius: 16,
          border: "1px solid rgba(212,115,93,0.08)",
          textAlign: "center",
        }}
      >
        <div
          style={{
            fontSize: 12,
            fontWeight: 500,
            color: C.mutedColor,
            letterSpacing: "0.15em",
            textTransform: "uppercase",
            marginBottom: 6,
          }}
        >
          {t.brand}
        </div>
        <div
          style={{
            fontSize: 18,
            fontWeight: 400,
            color: C.titleColor,
            letterSpacing: "0.02em",
            lineHeight: 1.5,
          }}
        >
          {language === "ja"
            ? view.header.title
            : L("Today's Heart Flow & Timing")}
        </div>
        <p
          style={{
            fontSize: 13,
            color: C.mutedColor,
            marginTop: 4,
            lineHeight: 1.6,
          }}
        >
          {language === "ja" ? view.header.subtext : t.subtext}
        </p>
      </motion.div>

      <div className="flex flex-col gap-5">
        {/* ─── 1) Daily Timing Flow ────────────────────────────────────── */}
        <SectionBlock
          index={0}
          title={t.dailyFlow}
          icon={<Waves className="w-5 h-5" style={{ color: C.accent }} />}
          iconBg="rgba(212,115,93,0.12)"
          background="#FAFAFA"
        >
          <p
            style={{
              fontSize: 15,
              lineHeight: 1.8,
              color: C.bodyColor,
              margin: 0,
            }}
          >
            {dailyTiming}
          </p>
        </SectionBlock>

        {/* ─── 2) Relationship Timing ────────────────────────────────── */}
        <SectionBlock
          index={1}
          title={t.relationship}
          icon={<Heart className="w-5 h-5" style={{ color: C.accent }} />}
          iconBg="rgba(212,115,93,0.12)"
          background={C.relationshipBg}
        >
          <p
            style={{
              fontSize: 15,
              lineHeight: 1.8,
              color: C.bodyColor,
              margin: 0,
            }}
          >
            {relationshipTiming}
          </p>
        </SectionBlock>

        {/* ─── 3) Gentle Luck Flow ────────────────────────────────────── */}
        <SectionBlock
          index={2}
          title={t.gentleLuck}
          icon={<Leaf className="w-5 h-5" style={{ color: "#6FAE8C" }} />}
          iconBg="rgba(111,174,140,0.15)"
          background={C.luckBg}
        >
          <p
            style={{
              fontSize: 15,
              lineHeight: 1.8,
              color: C.bodyColor,
              margin: 0,
            }}
          >
            {gentleLuck}
          </p>
        </SectionBlock>

        {/* ─── 4) Diary Section ────────────────────────────────────────── */}
        <SectionBlock
          index={3}
          title={t.diary}
          icon={
            <Sparkles className="w-5 h-5" style={{ color: C.mutedColor }} />
          }
          iconBg="rgba(138,122,115,0.12)"
          background="#FAFAFA"
        >
          <p
            style={{
              fontSize: 14,
              color: C.mutedColor,
              marginBottom: 4,
              lineHeight: 1.6,
            }}
          >
            {t.diaryPrompt}
          </p>
          <p
            style={{
              fontSize: 13,
              color: C.mutedColor,
              marginBottom: 16,
              opacity: 0.8,
            }}
          >
            {t.diaryHint}
          </p>
          <textarea
            value={diaryEntry}
            onChange={(e) =>
              setDiaryEntry(e.target.value.slice(0, DIARY_MAX_LENGTH))
            }
            placeholder={t.diaryPlaceholder}
            rows={3}
            maxLength={DIARY_MAX_LENGTH}
            className="w-full rounded-2xl border px-4 py-3 text-sm focus:outline-none transition-all"
            style={{
              borderColor: C.cardBorder,
              background: "#FFFFFF",
              color: C.titleColor,
              fontFamily: JP_FONT,
            }}
          />
          <div className="flex items-center justify-between mt-3">
            <button
              onClick={handleSaveDiary}
              className="rounded-full px-6 py-2.5 text-sm font-medium text-white transition-all hover:scale-105 active:scale-95"
              style={{
                background: star.primaryColor,
                boxShadow: `0 2px 12px ${star.primaryColor}33`,
              }}
            >
              {diarySaved ? t.diarySaved : t.saveDiary}
            </button>
            <span style={{ fontSize: 12, color: C.mutedColor }}>
              {diaryEntry.length} / {DIARY_MAX_LENGTH}
            </span>
          </div>
        </SectionBlock>

        {/* ─── 5) Footer ────────────────────────────────────────────────── */}
        <motion.div
          custom={4}
          variants={fadeUp}
          initial="hidden"
          animate="visible"
          style={{
            textAlign: "center",
            padding: "20px 16px",
          }}
        >
          <div
            style={{
              fontSize: 11,
              fontWeight: 400,
              color: C.mutedColor,
              letterSpacing: "0.1em",
              textTransform: "uppercase",
            }}
          >
            {t.brand}
          </div>
          <div
            style={{
              fontSize: 12,
              color: C.mutedColor,
              marginTop: 4,
              lineHeight: 1.6,
            }}
          >
            {t.footer}
          </div>
        </motion.div>
      </div>

      {/* ─── Actions ────────────────────────────────────────────────────── */}
      <motion.div
        className="flex justify-end mt-6"
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.5 }}
      >
        <button
          onClick={handleNewCheckIn}
          className="flex items-center justify-center gap-2 rounded-full py-2.5 px-6 text-sm font-medium transition-all hover:bg-gray-50 active:scale-95"
          style={{
            border: "1px solid rgba(43,33,29,0.1)",
            color: C.bodyColor,
            background: "transparent",
          }}
        >
          <RefreshCw className="w-3.5 h-3.5" />
          {t.refresh}
        </button>
      </motion.div>
    </AstriaJapanV2Shell>
  );
};

export default JPTimingFlowResult;
